import React from 'react';
import { DemoPreset, SimulationConfig } from '@cbm/shared';

// Demo preset configurations based on requirements 10.1-10.4
const DEMO_PRESETS: DemoPreset[] = [
  {
    id: 'healthy-baseline',
    name: 'Healthy Baseline',
    description: 'Normal operation across all tags with stable performance metrics',
    config: {
      machineId: '', // Will be set when applied
      usageProfile: 'normal',
      intervalMs: 1000,
      duration: 300000, // 5 minutes
    },
    expectedOutcomes: [
      'All telemetry tags remain within normal operating ranges',
      'No alerts generated during simulation period',
      'Stable baseline values established for future comparisons',
      'Demonstrates healthy equipment operation patterns'
    ]
  },
  {
    id: 'pattern-a-escalation',
    name: 'Pattern A - Escalation to NCR',
    description: 'Mechanical wear progression leading to automatic NCR creation',
    config: {
      machineId: '', // Will be set when applied
      usageProfile: 'aggressive',
      degradationPattern: 'A',
      intervalMs: 1000,
      duration: 900000, // 15 minutes
    },
    expectedOutcomes: [
      'Early alerts generated for mechanical drift (5-10 minutes)',
      'Mid alerts triggered for process instability (10-12 minutes)',
      'Late alerts escalated for sustained problems (12-15 minutes)',
      'Automatic NCR creation when late alert persistence threshold met',
      'Evidence links captured for audit trail'
    ]
  },
  {
    id: 'pattern-b-utility',
    name: 'Pattern B - Utility Instability',
    description: 'Air/vacuum system degradation affecting multiple process parameters',
    config: {
      machineId: '', // Will be set when applied
      usageProfile: 'poor_pm',
      degradationPattern: 'B',
      intervalMs: 1000,
      duration: 600000, // 10 minutes
    },
    expectedOutcomes: [
      'Correlated degradation across utility-dependent tags',
      'Pressure and flow rate instabilities detected',
      'Mid alerts for utility system performance issues',
      'Cross-tag correlation patterns visible in OEM dashboards',
      'Demonstrates utility infrastructure impact on production'
    ]
  },
  {
    id: 'pattern-c-masking',
    name: 'Pattern C - Operator Masking',
    description: 'Operator interventions masking underlying equipment issues',
    config: {
      machineId: '', // Will be set when applied
      usageProfile: 'normal',
      degradationPattern: 'C',
      intervalMs: 1000,
      duration: 1200000, // 20 minutes
    },
    expectedOutcomes: [
      'Initial degradation signals appear (0-5 minutes)',
      'Operator reset events mask underlying issues (5-15 minutes)',
      'Delayed failure detection due to masking behavior',
      'Late alerts eventually triggered despite interventions (15-20 minutes)',
      'Demonstrates importance of root cause analysis over symptom treatment'
    ]
  }
];

interface DemoPresetsProps {
  selectedMachine: { sk: string; displayName: string } | null;
  onPresetSelect: (preset: DemoPreset) => void;
  currentConfig: SimulationConfig | null;
  isRunning: boolean;
  className?: string;
}

export function DemoPresets({
  selectedMachine,
  onPresetSelect,
  currentConfig,
  isRunning,
  className = ''
}: DemoPresetsProps) {
  const handlePresetClick = (preset: DemoPreset) => {
    if (!selectedMachine || isRunning) return;

    const presetWithMachine: DemoPreset = {
      ...preset,
      config: {
        ...preset.config,
        machineId: selectedMachine.sk.replace('MACHINE#', '')
      }
    };

    onPresetSelect(presetWithMachine);
  };

  const isPresetActive = (preset: DemoPreset) => {
    if (!currentConfig) return false;
    
    return (
      currentConfig.usageProfile === preset.config.usageProfile &&
      currentConfig.degradationPattern === preset.config.degradationPattern &&
      currentConfig.intervalMs === preset.config.intervalMs &&
      currentConfig.duration === preset.config.duration
    );
  };

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="text-sm text-slate-400 mb-4">
        {!selectedMachine && 'Select a machine to enable demo presets'}
        {selectedMachine && isRunning && 'Stop simulation to change presets'}
        {selectedMachine && !isRunning && 'Click a preset to apply its configuration'}
      </div>

      <div className="grid grid-cols-1 gap-4">
        {DEMO_PRESETS.map((preset) => (
          <div
            key={preset.id}
            className={`border rounded-lg p-4 cursor-pointer transition-all ${
              !selectedMachine || isRunning
                ? 'bg-slate-800 border-slate-700 cursor-not-allowed opacity-50'
                : isPresetActive(preset)
                ? 'bg-blue-500/10 border-blue-500/30 shadow-lg shadow-blue-500/10'
                : 'bg-slate-800 border-slate-700 hover:border-slate-600 hover:shadow-lg'
            }`}
            onClick={() => handlePresetClick(preset)}
          >
            <div className="flex items-start justify-between mb-2">
              <h3 className="font-semibold text-white">{preset.name}</h3>
              {isPresetActive(preset) && (
                <span className="px-2 py-1 text-xs bg-blue-500/20 text-blue-400 rounded-full border border-blue-500/30">
                  Active
                </span>
              )}
            </div>
            
            <p className="text-sm text-slate-300 mb-3">{preset.description}</p>
            
            <div className="grid grid-cols-2 gap-4 text-xs text-slate-400 mb-3">
              <div>
                <span className="font-medium text-slate-300">Profile:</span> <span className="capitalize">{preset.config.usageProfile}</span>
              </div>
              <div>
                <span className="font-medium text-slate-300">Pattern:</span> {preset.config.degradationPattern || 'None'}
              </div>
              <div>
                <span className="font-medium text-slate-300">Duration:</span> {preset.config.duration ? `${preset.config.duration / 60000} min` : 'Infinite'}
              </div>
              <div>
                <span className="font-medium text-slate-300">Interval:</span> {preset.config.intervalMs}ms
              </div>
            </div>
            
            <div className="border-t border-slate-700 pt-3">
              <div className="text-xs font-medium text-slate-300 mb-2">Expected Outcomes:</div>
              <ul className="text-xs text-slate-400 space-y-1">
                {preset.expectedOutcomes.slice(0, 2).map((outcome, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-blue-400 mr-2">•</span>
                    {outcome}
                  </li>
                ))}
                {preset.expectedOutcomes.length > 2 && (
                  <li className="text-slate-500 italic">
                    +{preset.expectedOutcomes.length - 2} more outcomes...
                  </li>
                )}
              </ul>
            </div>
          </div>
        ))}
      </div>

      {/* Preset Details Guide */}
      <div className="mt-6 p-4 bg-slate-800 border border-slate-700 rounded-lg">
        <h4 className="font-medium text-white mb-3">Demo Scenario Guide</h4>
        <div className="text-sm text-slate-300 space-y-3">
          <div className="flex items-start space-x-3">
            <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0"></div>
            <div>
              <span className="font-medium text-white">Healthy Baseline:</span> Start here to establish normal operation patterns. 
              Creates baseline data for comparison with degradation scenarios.
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
            <div>
              <span className="font-medium text-white">Pattern A:</span> Best for demonstrating the complete alert escalation workflow 
              from early detection to NCR creation. Shows mechanical wear progression.
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
            <div>
              <span className="font-medium text-white">Pattern B:</span> Ideal for showing utility system impacts and cross-tag 
              correlations. Demonstrates infrastructure-related failures.
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
            <div>
              <span className="font-medium text-white">Pattern C:</span> Perfect for illustrating the challenges of operator 
              interventions and the importance of root cause analysis.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Hook for managing demo presets
export function useDemoPresets() {
  const [selectedPreset, setSelectedPreset] = React.useState<DemoPreset | null>(null);
  
  const applyPreset = (preset: DemoPreset) => {
    setSelectedPreset(preset);
    return preset.config;
  };

  const clearPreset = () => {
    setSelectedPreset(null);
  };

  return {
    selectedPreset,
    applyPreset,
    clearPreset,
    availablePresets: DEMO_PRESETS
  };
}